/**
 * PAYD — pay-as-you-drive, Hegic's third deployment.
 *
 * Each job is three reads off three photographs: the number plate, the
 * odometer and the VIN. The list gives the readings, the detail gives the
 * photographs behind them.
 *
 * Unlike claims and break-in this lives on the PORTAL host, answers a POST for
 * its list, and pages by PAGE NUMBER (break-in pages by offset). Every shape
 * here was probed live before it was written.
 *
 * Pure: no axios, no React, no clock.
 */

const asRows = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") return Object.values(raw);
  return [];
};

const int = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

/**
 * A reading, or the absence of one.
 *
 * The API says "not read" in at least FOUR ways — "Not_Found", "-", "None"
 * (a stringified Python None) and an empty string — so a caller checking for
 * some of them renders the others as if they were readings. The first version
 * of this missed "None", and job 5759137 in the fixture beside it duly showed
 * the word None where a number plate goes.
 */
const NOT_READ = new Set(["-", "not_found", "none", "null", "n/a", "nan"]);

export const toReading = (value) => {
  const text = typeof value === "string" ? value.trim() : "";
  if (!text || NOT_READ.has(text.toLowerCase())) return null;
  return text;
};

/**
 * One page of jobs.
 *
 * The three counters beside the rows are whole-dataset totals, not page
 * totals: 4939 plates of 4964 jobs. They are how many were READ, which is the
 * number this product is judged on.
 *
 * `*_status` is 4 on every row seen and its vocabulary is undocumented, so it
 * is carried through untouched and never rendered as a word — a made-up label
 * for an unknown code is worse than the code.
 */
export const toPaydJobs = (raw) => {
  const rows = asRows(raw?.data).filter((row) => row && typeof row === "object");
  return {
    rows: rows.map((row) => ({
      id: String(row.id ?? row.ref_num ?? ""),
      refNum: String(row.ref_num ?? ""),
      plate: toReading(row.lp_res),
      odometer: toReading(row.odo_res),
      vin: toReading(row.vin_res),
      plateStatus: row.lp_status ?? null,
      odometerStatus: row.odo_status ?? null,
      vinStatus: row.vin_status ?? null,
      reviewStatus: typeof row.review_status === "string" ? row.review_status : null,
      createdAt: typeof row.created_at === "string" ? row.created_at : null,
    })),
    total: int(raw?.results),
    // `offset` is the index of the first ROW, not a page number: page 2
    // answers 11. Deriving the page from it made the pager read "page 11 of
    // 497" and skip nine pages per click, so the page is stamped by the
    // caller, which knows what it asked for.
    rowOffset: int(raw?.offset) || 1,
    pageSize: int(raw?.page_size) || rows.length,
    totalPages: int(raw?.total_pages),
    read: {
      plate: int(raw?.NumberPlate),
      odometer: int(raw?.Odo),
      vin: int(raw?.Vin),
    },
  };
};

/** One job's photographs and what was read from each. */
export const toPaydDetail = (raw, refNum = "") => {
  if (!raw || typeof raw !== "object") return null;
  const field = (prefix, label) => ({
    key: prefix,
    label,
    value: toReading(raw[`${prefix}_res`]),
    image: typeof raw[`${prefix}_image`] === "string" ? raw[`${prefix}_image`] : null,
    review: toReading(raw[`${prefix}_review`]),
    reviewedBy: toReading(raw[`${prefix}_user_id`]),
    reviewedAt: toReading(raw[`${prefix}_updated_at`]),
  });

  return {
    refNum,
    fields: [
      field("lp", "Number plate"),
      field("odo", "Odometer"),
      field("vin", "VIN"),
    ],
    remarks: toReading(raw.remarks),
    closedBy: toReading(raw.review_closed_by),
    // A number here where the list sends a word ("Completed"); left as-is
    // rather than mapped to a vocabulary nobody has written down.
    reviewStatus: raw.review_status ?? null,
  };
};
